import React, { useEffect, useState, useCallback } from 'react';
import {
  BrandedViewerProvider,
  BrandedViewerLayout,
  BrandedViewerLoading,
  BrandedViewerNotFound,
  IMAGEMOTION_CONFIG,
} from '@rehub/branded-viewer';
import type { SharedVideoData } from '@rehub/branded-viewer';
import { fetchSharedVideo, recordShareAnalytics } from '../services/supabase';

interface SharedVideoViewerProps {
  shareToken: string;
}

export default function SharedVideoViewer({ shareToken }: SharedVideoViewerProps) {
  const [video, setVideo] = useState<SharedVideoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    async function load() {
      const data = await fetchSharedVideo(shareToken);
      if (!data) {
        setNotFound(true);
      } else {
        setVideo({
          project_id: data.project_id,
          project_name: data.project_name,
          video_url: data.final_video_url,
          logo_url: data.logo_url,
          created_at: data.created_at,
          is_promoted: data.is_promoted,
          brand_agency_name: data.brand_agency_name,
          brand_primary: data.brand_primary,
          brand_secondary: data.brand_secondary,
          brand_logo_position: data.brand_logo_position,
          brand_cta_label: data.brand_cta_label,
          brand_cta_url: data.brand_cta_url,
          brand_show_social: data.brand_show_social,
          audio_tracks: data.audio_tracks,
        });
      }
      setLoading(false);
    }
    load();
  }, [shareToken]);

  const handleAnalytics = useCallback(
    (eventType: 'view' | 'play' | 'complete' | 'share_click', extra?: any) => {
      recordShareAnalytics(shareToken, eventType, extra);
    },
    [shareToken]
  );

  if (loading) {
    return <BrandedViewerLoading primaryColor="#3b82f6" secondaryColor="#7c3aed" />;
  }

  if (notFound || !video) {
    return (
      <BrandedViewerNotFound
        appName={IMAGEMOTION_CONFIG.appName}
        appUrl={IMAGEMOTION_CONFIG.appUrl}
        appLogoUrl={IMAGEMOTION_CONFIG.appLogoUrl}
        primaryColor="#3b82f6"
        secondaryColor="#7c3aed"
      />
    );
  }

  return (
    <BrandedViewerProvider config={IMAGEMOTION_CONFIG} video={video}>
      <BrandedViewerLayout onAnalytics={handleAnalytics} />
    </BrandedViewerProvider>
  );
}
